import React, { useState } from 'react';
import { Rocket, Wallet, TrendingUp, CheckCircle, ChevronRight, ChevronLeft, Sparkles } from 'lucide-react';
import { useAuth } from '../AuthContext';
import { useCountry } from '../CountryContext';
import { getCurrencySymbol, getAccountTypes } from '../countryConfig';
import { collection, addDoc } from 'firebase/firestore';
import { db } from '../firebase';

const OnboardingWizard = ({ onComplete }) => {
  const { currentUser, userProfile, updateUserProfile } = useAuth();
  const { selectedCountry } = useCountry();
  const currencySymbol = getCurrencySymbol(selectedCountry);
  const configAccountTypes = getAccountTypes(selectedCountry);

  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(false);

  // Step 2: Account data
  const [accountData, setAccountData] = useState({
    name: '',
    type: '',
    institution: ''
  });
  const [createdAccount, setCreatedAccount] = useState(null);

  // Step 3: Investment data
  const [investmentData, setInvestmentData] = useState({
    name: '',
    amount: '',
    category: 'Stocks',
    risk: 'Medium',
    return: '8'
  });

  // Map account types
  const accountTypes = configAccountTypes ? configAccountTypes.map(acc => ({
    value: acc.id,
    label: acc.name,
    icon: acc.type === 'retirement' || acc.type === 'pension' ? '🏦' :
          acc.type === 'isa' ? '💰' :
          acc.type === 'health' ? '🏥' :
          acc.type === 'education' ? '🎓' :
          acc.type === 'crypto' ? '₿' :
          acc.type === 'taxable' ? '📈' : '💵',
    description: acc.description
  })) : [];

  // Investment categories with default returns
  const categories = [
    { value: 'Stocks', label: 'Stocks', defaultReturn: 8 },
    { value: 'Bonds', label: 'Bonds', defaultReturn: 4 },
    { value: 'Real Estate', label: 'Real Estate', defaultReturn: 6 },
    { value: 'Commodities', label: 'Commodities', defaultReturn: 5 },
    { value: 'Cryptocurrency', label: 'Cryptocurrency', defaultReturn: 10 },
    { value: 'Cash', label: 'Cash', defaultReturn: 1 }
  ];

  // Handle account type selection and auto-fill name
  const handleAccountTypeChange = (type) => {
    const selectedType = accountTypes.find(t => t.value === type);
    setAccountData({
      ...accountData,
      type,
      name: accountData.name || `My ${selectedType?.label || 'Account'}`
    });
  };

  // Handle category change and auto-set return
  const handleCategoryChange = (category) => {
    const selectedCategory = categories.find(c => c.value === category);
    setInvestmentData({
      ...investmentData,
      category,
      return: investmentData.return || String(selectedCategory?.defaultReturn || 8)
    });
  };

  // Step 2: Create Account
  const handleCreateAccount = async () => {
    if (!accountData.name || !accountData.type) {
      alert('Please fill in all required fields');
      return;
    }

    setLoading(true);
    try {
      // Determine account purpose from type
      const selectedAccountType = configAccountTypes.find(acc => acc.id === accountData.type);
      const accountPurpose = selectedAccountType?.type === 'retirement' || selectedAccountType?.type === 'pension'
        ? 'retirement'
        : selectedAccountType?.type === 'taxable'
        ? 'taxable'
        : selectedAccountType?.type === 'crypto'
        ? 'crypto'
        : 'savings';

      const account = {
        name: accountData.name,
        type: accountData.type,
        institution: accountData.institution || 'Not specified',
        accountPurpose,
        contributionAmount: 0,
        contributionFrequency: 'monthly',
        addedAt: new Date().toISOString()
      };

      // Save to Firebase
      const docRef = await addDoc(collection(db, 'users', currentUser.uid, 'accounts'), account);

      const accountWithId = { ...account, id: docRef.id };
      setCreatedAccount(accountWithId);

      // Move to next step
      setStep(3);
    } catch (error) {
      console.error('Error creating account:', error);
      alert('Failed to create account. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  // Step 3: Add Investment (Optional)
  const handleAddInvestment = async () => {
    if (!investmentData.name || !investmentData.amount) {
      alert('Please fill in investment name and amount');
      return;
    }

    setLoading(true);
    try {
      const investment = {
        id: Date.now(),
        name: investmentData.name,
        ticker: '',
        amount: parseFloat(investmentData.amount),
        return: parseFloat(investmentData.return),
        risk: investmentData.risk,
        category: investmentData.category,
        accountId: createdAccount.id,
        accountName: createdAccount.name,
        purchaseDate: new Date().toISOString().split('T')[0]
      };

      // Add to user's investments array
      const currentInvestments = userProfile?.investments || [];
      await updateUserProfile(currentUser.uid, {
        investments: [...currentInvestments, investment]
      });

      // Mark onboarding as completed
      await completeOnboarding();
    } catch (error) {
      console.error('Error adding investment:', error);
      alert('Failed to add investment. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  // Skip investment and complete onboarding
  const handleSkipInvestment = async () => {
    await completeOnboarding();
  };

  // Complete onboarding
  const completeOnboarding = async () => {
    setLoading(true);
    try {
      await updateUserProfile(currentUser.uid, {
        onboardingCompleted: true
      });
      onComplete();
    } catch (error) {
      console.error('Error completing onboarding:', error);
      alert('Failed to complete setup. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-gray-900 bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="bg-gradient-to-r from-blue-600 to-purple-600 text-white p-6 rounded-t-lg">
          <div className="flex items-center space-x-2 mb-2">
            <Sparkles className="w-6 h-6" />
            <h2 className="text-2xl font-bold">Welcome to PlanLens!</h2>
          </div>
          <p className="text-blue-100">Let's set up your portfolio in under 2 minutes</p>

          {/* Progress indicator */}
          <div className="mt-4 flex items-center space-x-2">
            <div className={`flex-1 h-2 rounded ${step >= 1 ? 'bg-white' : 'bg-blue-400'}`} />
            <div className={`flex-1 h-2 rounded ${step >= 2 ? 'bg-white' : 'bg-blue-400'}`} />
            <div className={`flex-1 h-2 rounded ${step >= 3 ? 'bg-white' : 'bg-blue-400'}`} />
          </div>
          <p className="text-sm text-blue-100 mt-2">Step {step} of 3</p>
        </div>

        {/* Content */}
        <div className="p-6">
          {/* Step 1: Welcome */}
          {step === 1 && (
            <div className="space-y-6">
              <div className="text-center">
                <Rocket className="w-16 h-16 text-blue-600 mx-auto mb-4" />
                <h3 className="text-2xl font-bold text-gray-900 mb-2">
                  Ready to take control of your financial future?
                </h3>
                <p className="text-gray-600 mb-6">
                  PlanLens helps you track your investments, plan for retirement, and achieve your financial goals.
                </p>
              </div>

              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                <h4 className="font-semibold text-blue-900 mb-2">What we'll do:</h4>
                <ul className="space-y-2 text-blue-800">
                  <li className="flex items-start">
                    <CheckCircle className="w-5 h-5 mr-2 flex-shrink-0 mt-0.5" />
                    <span><strong>Step 1:</strong> Create your first investment account</span>
                  </li>
                  <li className="flex items-start">
                    <CheckCircle className="w-5 h-5 mr-2 flex-shrink-0 mt-0.5" />
                    <span><strong>Step 2:</strong> Add your first investment (optional)</span>
                  </li>
                  <li className="flex items-start">
                    <CheckCircle className="w-5 h-5 mr-2 flex-shrink-0 mt-0.5" />
                    <span><strong>Step 3:</strong> Start tracking your wealth!</span>
                  </li>
                </ul>
              </div>

              <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
                <p className="text-sm text-yellow-800">
                  <strong>💡 Quick tip:</strong> Accounts are containers that hold your investments.
                  For example, a 401(k) account might contain stock and bond investments.
                </p>
              </div>

              <button
                onClick={() => setStep(2)}
                className="w-full bg-gradient-to-r from-blue-600 to-purple-600 text-white py-3 px-6 rounded-lg font-semibold hover:from-blue-700 hover:to-purple-700 transition flex items-center justify-center space-x-2"
              >
                <span>Get Started</span>
                <ChevronRight className="w-5 h-5" />
              </button>
            </div>
          )}

          {/* Step 2: Create Account */}
          {step === 2 && (
            <div className="space-y-6">
              <div className="text-center mb-6">
                <Wallet className="w-12 h-12 text-blue-600 mx-auto mb-3" />
                <h3 className="text-xl font-bold text-gray-900">Create Your First Account</h3>
                <p className="text-gray-600">This will be a container for your investments</p>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Account Type <span className="text-red-500">*</span>
                </label>
                <select
                  value={accountData.type}
                  onChange={(e) => handleAccountTypeChange(e.target.value)}
                  className="w-full border border-gray-300 rounded-lg p-3 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                >
                  <option value="">Select account type...</option>
                  {accountTypes.map(type => (
                    <option key={type.value} value={type.value}>
                      {type.icon} {type.label}
                    </option>
                  ))}
                </select>
                {accountData.type && (
                  <p className="text-sm text-gray-500 mt-1">
                    {accountTypes.find(t => t.value === accountData.type)?.description}
                  </p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Account Name <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  value={accountData.name}
                  onChange={(e) => setAccountData({ ...accountData, name: e.target.value })}
                  placeholder="e.g., My 401(k), Main Brokerage"
                  className="w-full border border-gray-300 rounded-lg p-3 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Institution (Optional)
                </label>
                <input
                  type="text"
                  value={accountData.institution}
                  onChange={(e) => setAccountData({ ...accountData, institution: e.target.value })}
                  placeholder="e.g., Vanguard, Fidelity, Charles Schwab"
                  className="w-full border border-gray-300 rounded-lg p-3 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>

              <div className="flex space-x-3">
                <button
                  onClick={() => setStep(1)}
                  className="flex-1 bg-gray-200 text-gray-700 py-3 px-6 rounded-lg font-semibold hover:bg-gray-300 transition flex items-center justify-center space-x-2"
                >
                  <ChevronLeft className="w-5 h-5" />
                  <span>Back</span>
                </button>
                <button
                  onClick={handleCreateAccount}
                  disabled={loading || !accountData.name || !accountData.type}
                  className="flex-1 bg-gradient-to-r from-blue-600 to-purple-600 text-white py-3 px-6 rounded-lg font-semibold hover:from-blue-700 hover:to-purple-700 transition disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center space-x-2"
                >
                  <span>{loading ? 'Creating...' : 'Create Account'}</span>
                  <ChevronRight className="w-5 h-5" />
                </button>
              </div>
            </div>
          )}

          {/* Step 3: Add Investment (Optional) */}
          {step === 3 && (
            <div className="space-y-6">
              <div className="text-center mb-6">
                <TrendingUp className="w-12 h-12 text-green-600 mx-auto mb-3" />
                <h3 className="text-xl font-bold text-gray-900">Add Your First Investment</h3>
                <p className="text-gray-600">Add an investment to {createdAccount?.name} (you can skip this step)</p>
              </div>

              <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                <p className="text-sm text-green-800">
                  ✅ Account created: <strong>{createdAccount?.name}</strong>
                </p>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Investment Name <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  value={investmentData.name}
                  onChange={(e) => setInvestmentData({ ...investmentData, name: e.target.value })}
                  placeholder="e.g., S&P 500 Index Fund, Apple Stock"
                  className="w-full border border-gray-300 rounded-lg p-3 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Current Value <span className="text-red-500">*</span>
                </label>
                <div className="relative">
                  <span className="absolute left-3 top-3 text-gray-500">{currencySymbol}</span>
                  <input
                    type="number"
                    value={investmentData.amount}
                    onChange={(e) => setInvestmentData({ ...investmentData, amount: e.target.value })}
                    placeholder="0"
                    className="w-full border border-gray-300 rounded-lg p-3 pl-8 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Category
                  </label>
                  <select
                    value={investmentData.category}
                    onChange={(e) => handleCategoryChange(e.target.value)}
                    className="w-full border border-gray-300 rounded-lg p-3 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  >
                    {categories.map(cat => (
                      <option key={cat.value} value={cat.value}>{cat.label}</option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Risk Level
                  </label>
                  <select
                    value={investmentData.risk}
                    onChange={(e) => setInvestmentData({ ...investmentData, risk: e.target.value })}
                    className="w-full border border-gray-300 rounded-lg p-3 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  >
                    <option value="Low">Low</option>
                    <option value="Medium">Medium</option>
                    <option value="High">High</option>
                  </select>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Expected Annual Return (%)
                </label>
                <input
                  type="number"
                  value={investmentData.return}
                  onChange={(e) => setInvestmentData({ ...investmentData, return: e.target.value })}
                  placeholder="8"
                  step="0.1"
                  className="w-full border border-gray-300 rounded-lg p-3 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
                <p className="text-sm text-gray-500 mt-1">
                  Default for {investmentData.category}: {categories.find(c => c.value === investmentData.category)?.defaultReturn}%
                </p>
              </div>

              <div className="flex space-x-3">
                <button
                  onClick={handleSkipInvestment}
                  disabled={loading}
                  className="flex-1 bg-gray-200 text-gray-700 py-3 px-6 rounded-lg font-semibold hover:bg-gray-300 transition disabled:opacity-50"
                >
                  {loading ? 'Please wait...' : 'Skip for Now'}
                </button>
                <button
                  onClick={handleAddInvestment}
                  disabled={loading || !investmentData.name || !investmentData.amount}
                  className="flex-1 bg-gradient-to-r from-blue-600 to-purple-600 text-white py-3 px-6 rounded-lg font-semibold hover:from-blue-700 hover:to-purple-700 transition disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {loading ? 'Saving...' : 'Add Investment & Finish'}
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default OnboardingWizard;
